import { useEffect, useMemo, useState } from "react";
import EmptyState from "../../../components/EmptyState";
import type { ProvisionJobStatus, ProvisionJobSummary } from "../types";
import JobCard from "./JobCard";
import "../../../App.css";

const PAGE_SIZE = 6;

type StatusFilter = "all" | ProvisionJobStatus;
type ViewMode = "grid" | "list";

interface JobListProps {
  jobs: ProvisionJobSummary[];
}

const JobList = ({ jobs }: JobListProps) => {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [view, setView] = useState<ViewMode>("grid");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((job) => {
      if (status !== "all" && job.status !== status) return false;
      if (!q) return true;
      return (
        job.name.toLowerCase().includes(q) ||
        job.id.toLowerCase().includes(q) ||
        job.createdBy.email.toLowerCase().includes(q) ||
        job.createdBy.name.toLowerCase().includes(q)
      );
    });
  }, [jobs, query, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pageClamped = Math.min(page, totalPages);
  const sliceStart = (pageClamped - 1) * PAGE_SIZE;
  const pageJobs = filtered.slice(sliceStart, sliceStart + PAGE_SIZE);

  if (jobs.length === 0) {
    return (
      <EmptyState
        title="No provisioning jobs yet"
        description="Upload a provisioning CSV with New upload to create your first job."
      />
    );
  }

  const rangeLabel =
    filtered.length === 0
      ? "0 jobs"
      : `Showing ${sliceStart + 1}-${Math.min(sliceStart + PAGE_SIZE, filtered.length)} of ${filtered.length} job${
          filtered.length === 1 ? "" : "s"
        }`;

  return (
    <div>
      <div className="job-list-toolbar">
        <div className="job-list-toolbar-left">
          <input
            type="search"
            className="job-list-search"
            placeholder="Search jobs by name or ID…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            aria-label="Search jobs"
          />
          <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.85rem", color: "#64748b" }}>
            <span style={{ fontWeight: 600 }}>Status</span>
            <select
              className="job-list-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
                setPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </label>
        </div>
        <div className="job-list-view-toggle" role="group" aria-label="Layout">
          <button type="button" aria-pressed={view === "grid"} onClick={() => setView("grid")} title="Grid view">
            ⊞
          </button>
          <button type="button" aria-pressed={view === "list"} onClick={() => setView("list")} title="List view">
            ☰
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            border: "1px dashed #cbd5e1",
            borderRadius: "1rem",
            padding: "2rem",
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc"
          }}
        >
          No jobs match your filters. Try clearing search or setting status to All.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "1.25rem",
            alignItems: "stretch",
            gridTemplateColumns: view === "grid" ? "repeat(auto-fill, minmax(320px, 1fr))" : "1fr"
          }}
        >
          {pageJobs.map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}

      {filtered.length > 0 ? (
        <nav className="job-list-pagination" aria-label="Pagination">
          <span>{rangeLabel}</span>
          <div className="job-list-page-btns">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={pageClamped <= 1}
              aria-label="First page"
            >
              «
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageClamped <= 1}
              aria-label="Previous page"
            >
              ‹
            </button>
            {(() => {
              const visible = (() => {
                if (totalPages <= 7) {
                  return Array.from({ length: totalPages }, (_, i) => i + 1) as (number | "ellipsis")[];
                }
                const set = new Set([
                  1,
                  2,
                  totalPages - 1,
                  totalPages,
                  pageClamped,
                  pageClamped - 1,
                  pageClamped + 1
                ]);
                const nums = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
                const out: (number | "ellipsis")[] = [];
                for (let i = 0; i < nums.length; i++) {
                  if (i > 0 && nums[i] - nums[i - 1] > 1) out.push("ellipsis");
                  out.push(nums[i]);
                }
                return out;
              })();
              return visible.map((item, idx) =>
                item === "ellipsis" ? (
                  <span key={`e-${idx}`} style={{ padding: "0 0.15rem", color: "#94a3b8", fontWeight: 600 }} aria-hidden>
                    …
                  </span>
                ) : (
                  <button key={item} type="button" data-active={item === pageClamped} onClick={() => setPage(item)}>
                    {item}
                  </button>
                )
              );
            })()}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageClamped >= totalPages}
              aria-label="Next page"
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={pageClamped >= totalPages}
              aria-label="Last page"
            >
              »
            </button>
          </div>
        </nav>
      ) : null}
    </div>
  );
};

export default JobList;
